import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";

function pick(obj, ...keys) {
  if (obj == null) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function getDataset(response) {
  return response?.data ?? response?.Data ?? response;
}

export function getTableRows(dataset, tableNames = [], tableIndex = -1) {
  if (!dataset || typeof dataset !== "object") return [];

  for (const name of tableNames) {
    const direct = dataset[name] ?? dataset[name?.toLowerCase?.()];
    if (Array.isArray(direct)) return direct;
    if (direct?.Rows && Array.isArray(direct.Rows)) return direct.Rows;
  }

  if (Array.isArray(dataset.Tables)) {
    for (const name of tableNames) {
      const table = dataset.Tables.find(
        (t) =>
          String(t?.TableName || t?.Name || t?.tableName || "")
            .toLowerCase() === String(name).toLowerCase(),
      );
      if (Array.isArray(table?.Rows)) return table.Rows;
    }
    if (tableIndex >= 0 && Array.isArray(dataset.Tables[tableIndex]?.Rows)) {
      return dataset.Tables[tableIndex].Rows;
    }
    const first = dataset.Tables.find((t) => Array.isArray(t?.Rows));
    if (first?.Rows) return first.Rows;
  }

  const positionalKeys = ["Table", "Table1", "Table2", "Table3", "Table4"];
  if (tableIndex >= 0 && tableIndex < positionalKeys.length) {
    const key = positionalKeys[tableIndex];
    const alt = dataset[key] ?? dataset[key?.toLowerCase?.()];
    if (Array.isArray(alt)) return alt;
    if (alt?.Rows && Array.isArray(alt.Rows)) return alt.Rows;
  }

  if (Array.isArray(dataset)) return dataset;
  return [];
}

/** True when top-level success=1, or SuccessStory.Success indicates SP success. */
function assertApiSuccess(response, fallbackMessage) {
  if (utils.isApiSuccess(response)) return response;

  const story = getSuccessStoryRow(getDataset(response));
  const storySuccess = story?.Success ?? story?.success;
  if (storySuccess != null && Number(storySuccess) === 1) {
    return response;
  }

  throw new Error(utils.getApiResponseMessage(response, fallbackMessage));
}

function baseContext(userData) {
  return {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
  };
}

/** Legacy VolunteerActivity ActionType values (usp_ERec_VolunteerActivity_NEWUI). */
export const VOLUNTEER_ACTIVITY_ACTIONS = {
  ACTIVE_LIST: "VolunteerList",
  INACTIVE_LIST: "InActiveVolunteerList",
  BY_ID_INFO: "VolunteerByVolIdinfo",
  NOTES: "VolunteerNotes",
  EMAIL_SMS_LOG: "EmailSMSLog",
  ACTIVITY_LOG: "VolunteerActivityLog",
};

/** Legacy SaveVolunteer ActionType values (usp_ERec_VolunteerRegistration_NEWUI). */
export const SAVE_VOLUNTEER_ACTIONS = {
  INSERT: "InsertVolunteer",
  UPDATE: "UpdateVolunteer",
  CHANGE_STATUS: "ChangeStatus",
};

/** Legacy ParticipantsinfoAPI ActionType values. */
export const PARTICIPANTS_INFO_ACTIONS = {
  LIST: "ParticipantsList",
  ARCHIVE_LIST: "ArchiveParticipantsList",
  COMMUNICATED_LIST: "CommunicatedParticipantsList",
  DETAIL: "ParticipantDetail",
  COMM_DETAIL: "CommParticipantDetail",
  SAVE: "AddUpdateParticipants",
  FILTER_VOLUNTEERS: "GetFilterBasedVolList",
  ARCHIVE: "ArchiveParticipant",
  UNARCHIVE: "unArchiveParticipant",
};

/** Legacy TemplateSetupAPI ActionType values. */
export const TEMPLATE_SETUP_ACTIONS = {
  LIST: "GetTemplatelist",
  ARCHIVED_LIST: "GetArchiveTemplatelist",
  BY_ID: "GetTemplate",
  ARCHIVE: "ArchieveTemplate",
  UNARCHIVE: "UnArchieveTemplate",
  CREATE_EMAIL: "CreateEmailTemplate",
  CREATE_SMS: "CreateSMSTemplate",
};

/** Legacy TemplateForCommunication ActionType values. */
export const TEMPLATE_COMM_ACTIONS = {
  LIST: "GetTemplates",
  BY_ID: "GetTemplatesById",
};

/** Legacy VolunteerByParticipant ActionType values. */
export const COMM_FILTER_ACTIONS = {
  GET_VOLUNTEERS: "GetVolunteerWithFilter",
  SAVE_FILTER: "SaveCommunicationFilter",
};

export async function volunteerActivity(userData, payload = {}) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.VOLUNTEER_ACTIVITY, {
    ...baseContext(userData),
    ...payload,
  });
  assertApiSuccess(response, "Could not load participant data.");
  return getDataset(response);
}

export async function fetchActiveVolunteers(userData) {
  const dataset = await volunteerActivity(userData, {
    ActionType: VOLUNTEER_ACTIVITY_ACTIONS.ACTIVE_LIST,
  });
  return getTableRows(dataset, ["VolunteerList"], 0);
}

export async function fetchInactiveVolunteers(userData) {
  const dataset = await volunteerActivity(userData, {
    ActionType: VOLUNTEER_ACTIVITY_ACTIONS.INACTIVE_LIST,
  });
  return getTableRows(dataset, ["VolunteerList"], 0);
}

/** Active + inactive/removed lists, deduped by VolunteerDemRecordId. */
export async function fetchAllVolunteers(userData) {
  const [activeResult, inactiveResult] = await Promise.allSettled([
    fetchActiveVolunteers(userData),
    fetchInactiveVolunteers(userData),
  ]);

  if (activeResult.status === "rejected" && inactiveResult.status === "rejected") {
    throw activeResult.reason || inactiveResult.reason;
  }

  const active =
    activeResult.status === "fulfilled" && Array.isArray(activeResult.value)
      ? activeResult.value
      : [];
  const inactive =
    inactiveResult.status === "fulfilled" && Array.isArray(inactiveResult.value)
      ? inactiveResult.value
      : [];

  const byId = new Map();
  let fallback = 0;
  for (const row of [...active, ...inactive]) {
    const id = getVolunteerRowId(row);
    if (id) {
      byId.set(id, row);
    } else {
      byId.set(`__row_${fallback++}`, row);
    }
  }
  return [...byId.values()];
}

export async function fetchVolunteerProfile(userData, volunteerDemRecordId) {
  const dataset = await volunteerActivity(userData, {
    ActionType: VOLUNTEER_ACTIVITY_ACTIONS.BY_ID_INFO,
    VolunteerDemRecordId: volunteerDemRecordId,
  });
  return {
    volunteer: getTableRows(dataset, ["VolunteerList"], 0)[0] ?? null,
    orgInfo: getTableRows(dataset, ["Orginfo"], 1)[0] ?? null,
  };
}

export async function fetchVolunteerNotes(userData, volunteerDemRecordId) {
  const dataset = await volunteerActivity(userData, {
    ActionType: VOLUNTEER_ACTIVITY_ACTIONS.NOTES,
    VolunteerDemRecordId: volunteerDemRecordId,
  });
  return getTableRows(dataset, ["VolunteerNotes"], 0);
}

export async function fetchVolunteerCommunications(userData, volunteerDemRecordId) {
  const dataset = await volunteerActivity(userData, {
    ActionType: VOLUNTEER_ACTIVITY_ACTIONS.EMAIL_SMS_LOG,
    VolunteerDemRecordId: volunteerDemRecordId,
  });
  return getTableRows(dataset, ["EmailSMSLog"], 0);
}

/**
 * Participant activity / audit trail.
 * Prefer legacy VolunteerActivity ActionType (same as Web GetVolunteerActivityLog),
 * then fall back to GetVolunteerAuditLog.
 */
export async function fetchVolunteerActivityLog(userData, volunteerDemRecordId) {
  try {
    const dataset = await volunteerActivity(userData, {
      ActionType: VOLUNTEER_ACTIVITY_ACTIONS.ACTIVITY_LOG,
      VolunteerDemRecordId: volunteerDemRecordId,
    });
    const rows = getTableRows(dataset, ["VolunteerActivityLog"], 0);
    if (rows.length) return rows;
  } catch {
    /* fall through to dedicated audit endpoint */
  }

  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.GET_VOLUNTEER_AUDIT_LOG, {
    VolunteerIdRef: volunteerDemRecordId,
    OrganizationId: userData?.OrganizationId,
    OrganizationIdRef: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
  });
  assertApiSuccess(response, "Could not load activity log.");
  const dataset = getDataset(response);
  // Endpoint may return a named table or a bare table/array.
  const rows = getTableRows(dataset, ["VolunteerActivityLog", "AuditLog", "Table"], 0);
  if (rows.length) return rows;
  if (Array.isArray(dataset)) return dataset;
  if (Array.isArray(dataset?.Table)) return dataset.Table;
  if (Array.isArray(dataset?.Rows)) return dataset.Rows;
  return [];
}

/** @deprecated Use fetchVolunteerActivityLog */
export async function fetchVolunteerAuditLog(userData, volunteerDemRecordId) {
  return fetchVolunteerActivityLog(userData, volunteerDemRecordId);
}

export async function saveVolunteer(userData, payload) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SAVE_VOLUNTEER, {
    ...baseContext(userData),
    ...payload,
  });
  assertApiSuccess(response, "Could not save participant.");
  return getDataset(response);
}

export async function insertVolunteer(userData, form) {
  return saveVolunteer(userData, {
    ActionType: SAVE_VOLUNTEER_ACTIONS.INSERT,
    ...form,
  });
}

export async function updateVolunteer(userData, form) {
  return saveVolunteer(userData, {
    ActionType: SAVE_VOLUNTEER_ACTIONS.UPDATE,
    ...form,
  });
}

export async function changeVolunteerStatus(userData, volunteerDemRecordId, volStatus) {
  return saveVolunteer(userData, {
    ActionType: SAVE_VOLUNTEER_ACTIONS.CHANGE_STATUS,
    VolunteerDemRecordId: volunteerDemRecordId,
    VolStatus: volStatus,
  });
}

export async function participantsInfo(
  userData,
  payload = {},
  fallbackMessage = "Could not load feasibility data.",
) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.PARTICIPANTS_INFO, {
    ...baseContext(userData),
    ...payload,
  });
  assertApiSuccess(response, fallbackMessage);
  return getDataset(response);
}

export async function fetchFeasibilityList(userData) {
  const dataset = await participantsInfo(userData, {
    ActionType: PARTICIPANTS_INFO_ACTIONS.LIST,
  });
  return {
    rows: getTableRows(dataset, ["ParticipantList"], 0),
    postCodes: getTableRows(dataset, ["PostCodeList"], 1),
  };
}

export async function fetchFeasibilityDetail(userData, participantId) {
  const dataset = await participantsInfo(userData, {
    ActionType: PARTICIPANTS_INFO_ACTIONS.DETAIL,
    ParticipantId: participantId,
  });
  return {
    participant: getTableRows(dataset, ["ParticipantList"], 0)[0] ?? null,
    medInfo: getTableRows(dataset, ["ParticipantMedInfo"], 1),
    recentFilters: getTableRows(dataset, ["RecentFilters"], 2),
  };
}

/** Expand postcodes so "CH48 4EL" also matches "CH484EL" (SP uses exact IN). */
export function expandPostcodeVariants(codes) {
  const values = Array.isArray(codes)
    ? codes
    : String(codes || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
  const out = new Set();
  values.forEach((raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return;
    const upper = trimmed.toUpperCase();
    const spaced = upper.replace(/\s+/g, " ").trim();
    const compact = upper.replace(/\s+/g, "");
    out.add(trimmed);
    out.add(upper);
    out.add(spaced);
    if (compact) out.add(compact);
  });
  return [...out];
}

/**
 * SP usp_ERec_Participantsinfo_NEWUI treats BMI 0 as "not set".
 * Empty Gender/PostCode must be null — "" wrongly applies IN ('') filters.
 * Postcode variants are for Run Query matching only — never persist expanded codes.
 */
export function normalizeFeasibilityPayload(payload = {}, options = {}) {
  const expandPostcodes = options.expandPostcodes !== false;
  const minBmi = String(payload.MinBMIValue ?? "").trim();
  const maxBmi = String(payload.BMIValue ?? "").trim();
  const gender = String(payload.Gender ?? "").trim();
  const postCodeRaw = String(payload.PostCode ?? "").trim();
  const selectedPostCodes = postCodeRaw
    ? [
        ...new Set(
          postCodeRaw
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
        ),
      ]
    : [];
  const postCodes = expandPostcodes
    ? expandPostcodeVariants(selectedPostCodes)
    : selectedPostCodes;
  const minAge = Number(payload.MinAgelimit);
  const maxAge = Number(payload.MaxAgeLimit);

  return {
    ...payload,
    ParticipantId: String(payload.ParticipantId ?? "").trim() || null,
    MinAgelimit: Number.isFinite(minAge) && minAge > 0 ? minAge : 0,
    MaxAgeLimit: Number.isFinite(maxAge) && maxAge > 0 ? maxAge : 0,
    // Legacy / SP: empty BMI → 0 (skip filter); set values as numeric strings.
    MinBMIValue: minBmi !== "" && !Number.isNaN(Number(minBmi)) ? String(Number(minBmi)) : "0",
    BMIValue: maxBmi !== "" && !Number.isNaN(Number(maxBmi)) ? String(Number(maxBmi)) : "0",
    Gender: gender || null,
    PostCode: postCodes.length ? postCodes.join(",") : null,
    MedTerms: Array.isArray(payload.MedTerms) ? payload.MedTerms : [],
  };
}

export async function saveFeasibility(userData, payload) {
  const dataset = await participantsInfo(
    userData,
    {
      ActionType: PARTICIPANTS_INFO_ACTIONS.SAVE,
      // Persist selected postcodes as-is (legacy). Expand only for Run Query.
      ...normalizeFeasibilityPayload(payload, { expandPostcodes: false }),
    },
    "Could not save feasibility query.",
  );
  const story = getTableRows(dataset, ["SuccessStory"], 0)[0] ?? null;
  const ok = String(story?.Success ?? story?.success ?? "") === "1";
  if (story && !ok) {
    throw new Error(
      String(story?.MSG || story?.Msg || story?.msg || "").trim() ||
        "Could not save feasibility query.",
    );
  }
  return story ?? dataset;
}

async function setFeasibilityArchiveState(userData, participantId, actionType) {
  const dataset = await participantsInfo(
    userData,
    {
      ActionType: actionType,
      ParticipantId: String(participantId || "").trim() || null,
    },
    actionType === PARTICIPANTS_INFO_ACTIONS.ARCHIVE
      ? "Could not archive feasibility."
      : "Could not activate feasibility.",
  );
  const story = getTableRows(dataset, ["SuccessStory"], 0)[0] ?? null;
  const ok = String(story?.Success ?? story?.success ?? "") === "1";
  if (story && !ok) {
    throw new Error(
      String(story?.MSG || story?.Msg || story?.msg || "").trim() ||
        "Could not update feasibility status.",
    );
  }
  return story ?? dataset;
}

export async function archiveFeasibility(userData, participantId) {
  return setFeasibilityArchiveState(
    userData,
    participantId,
    PARTICIPANTS_INFO_ACTIONS.ARCHIVE,
  );
}

export async function unarchiveFeasibility(userData, participantId) {
  return setFeasibilityArchiveState(
    userData,
    participantId,
    PARTICIPANTS_INFO_ACTIONS.UNARCHIVE,
  );
}

export async function fetchFeasibilityVolunteers(userData, payload) {
  const dataset = await participantsInfo(
    userData,
    {
      ActionType: PARTICIPANTS_INFO_ACTIONS.FILTER_VOLUNTEERS,
      ...normalizeFeasibilityPayload(payload, { expandPostcodes: true }),
    },
    "Could not run feasibility query.",
  );
  return getTableRows(dataset, ["VolunteerList"], 0);
}

export async function fetchCommFeasibilityDetail(userData, participantId) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.PARTICIPANTS_INFO, {
    ...baseContext(userData),
    ActionType: PARTICIPANTS_INFO_ACTIONS.COMM_DETAIL,
    ParticipantId: String(participantId || "").trim(),
  });
  const dataset = getDataset(response);
  const participantRows = getTableRows(dataset, ["ParticipantList"], 0);
  // Don't hard-fail when tables are present but top-level success is odd.
  if (!participantRows.length && !utils.isApiSuccess(response)) {
    assertApiSuccess(response, "Could not load communication details.");
  }
  return {
    participant: participantRows[0] ?? null,
    medInfo: getTableRows(dataset, ["ParticipantMedInfo"], 1),
    recentFilters: getTableRows(dataset, ["RecentFilters"], 2),
    templates: getTableRows(dataset, ["TemplateList"], 3),
  };
}

export async function templateSetup(userData, payload = {}) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.TEMPLATE_SETUP, {
    ...baseContext(userData),
    ...payload,
  });
  assertApiSuccess(response, "Could not load campaign templates.");
  return getDataset(response);
}

function looksLikeTemplateRow(row) {
  if (!row || typeof row !== "object") return false;
  return (
    pick(row, "TemplateId", "templateId", "TemplateName", "templateName") !=
    null
  );
}

function extractTemplateListRows(dataset) {
  const named = getTableRows(dataset, ["TemplateList", "templateList"], 0);
  if (named.length) return named;

  // Positional DataSet tables (Table / Table1) when TableName is dropped in JSON.
  const positional = getTableRows(dataset, ["Table", "Table1"], 0);
  if (positional.length && looksLikeTemplateRow(positional[0])) {
    return positional;
  }

  // Some serializers nest DataSet tables one level deeper.
  if (dataset && typeof dataset === "object" && !Array.isArray(dataset)) {
    for (const value of Object.values(dataset)) {
      if (!value || typeof value !== "object") continue;
      if (Array.isArray(value) && value.length && looksLikeTemplateRow(value[0])) {
        return value;
      }
      const nested = getTableRows(value, ["TemplateList", "templateList", "Table"], 0);
      if (nested.length && looksLikeTemplateRow(nested[0])) return nested;
    }
  }

  if (Array.isArray(dataset) && dataset.length && looksLikeTemplateRow(dataset[0])) {
    return dataset;
  }
  return [];
}

async function fetchTemplateListByAction(userData, actionType) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.TEMPLATE_SETUP, {
    ...baseContext(userData),
    ActionType: actionType,
  });
  const dataset = getDataset(response);
  const rows = extractTemplateListRows(dataset);
  if (rows.length) return rows;

  // Empty list is valid (legacy may return success=0 / "Try Again" when no rows).
  if (utils.isApiSuccess(response)) return [];
  const story = getSuccessStoryRow(dataset);
  if (story && Number(story.Success ?? story.success) === 1) return [];
  return [];
}

export async function fetchCampaignTemplates(userData) {
  return fetchTemplateListByAction(userData, TEMPLATE_SETUP_ACTIONS.LIST);
}

export async function fetchArchivedCampaignTemplates(userData) {
  return fetchTemplateListByAction(
    userData,
    TEMPLATE_SETUP_ACTIONS.ARCHIVED_LIST,
  );
}

/**
 * Active + archived campaign templates, tagged with CampaignStatus.
 * Fetches sequentially (parallel first-nav calls were intermittently
 * returning only the archived list).
 */
export async function fetchAllCampaignTemplates(userData) {
  let active = [];
  let archived = [];
  let activeError = null;
  let archivedError = null;

  try {
    active = await fetchCampaignTemplates(userData);
  } catch (err) {
    activeError = err;
    active = [];
  }

  // First navigation sometimes returns an empty active list while archived
  // succeeds — one quick retry covers that race without slowing the happy path.
  if (!active.length) {
    try {
      await new Promise((resolve) => {
        const wait = typeof window !== "undefined" ? window.setTimeout : setTimeout;
        wait(resolve, 120);
      });
      const retry = await fetchCampaignTemplates(userData);
      if (retry.length) {
        active = retry;
        activeError = null;
      }
    } catch (err) {
      if (!activeError) activeError = err;
    }
  }

  try {
    archived = await fetchArchivedCampaignTemplates(userData);
  } catch (err) {
    archivedError = err;
    archived = [];
  }

  if (!active.length && !archived.length && (activeError || archivedError)) {
    throw activeError || archivedError;
  }

  const byId = new Map();
  let fallback = 0;
  const rowId = (row) =>
    String(pick(row, "TemplateId", "templateId") || "").trim();

  for (const row of active) {
    const id = rowId(row) || `__active_${fallback++}`;
    byId.set(id, { ...row, CampaignStatus: "Active" });
  }
  for (const row of archived) {
    const id = rowId(row) || `__archived_${fallback++}`;
    // Archived wins if the same template appears in both lists.
    byId.set(id, { ...row, CampaignStatus: "Archived" });
  }

  return [...byId.values()];
}

export async function archiveCampaignTemplate(userData, templateId) {
  const dataset = await templateSetup(userData, {
    ActionType: TEMPLATE_SETUP_ACTIONS.ARCHIVE,
    TemplateId: templateId,
  });
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? dataset;
}

export async function unarchiveCampaignTemplate(userData, templateId) {
  const dataset = await templateSetup(userData, {
    ActionType: TEMPLATE_SETUP_ACTIONS.UNARCHIVE,
    TemplateId: templateId,
  });
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? dataset;
}

export async function createCampaignTemplate(userData, form) {
  const type = String(form?.TemplateType || "").trim().toLowerCase();
  const isSms = type === "sms";
  const dataset = await templateSetup(userData, {
    ActionType: isSms
      ? TEMPLATE_SETUP_ACTIONS.CREATE_SMS
      : TEMPLATE_SETUP_ACTIONS.CREATE_EMAIL,
    TemplateId: form?.TemplateId || null,
    TemplateName: String(form?.TemplateName || "").trim(),
    TemplateType: isSms ? "SMS" : "Email",
    SubjectName: isSms ? "" : String(form?.SubjectName || "").trim(),
    EmailTemplateText: isSms ? "" : String(form?.EmailTemplateText || "").trim(),
    SMSTemplateText: isSms ? String(form?.SMSTemplateText || "").trim() : "",
    TemplateAttachmentUrl: form?.TemplateAttachmentUrl || "",
  });
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? dataset;
}

export async function fetchCampaignTemplateById(userData, templateId) {
  const dataset = await templateSetup(userData, {
    ActionType: TEMPLATE_SETUP_ACTIONS.BY_ID,
    TemplateId: templateId,
  });
  return getTableRows(dataset, ["TemplateList"], 0)[0] ?? null;
}

export async function templateForCommunication(userData, payload = {}) {
  const response = await api.post(
    API_ENDPOINTS.ERECRUITMENT.TEMPLATE_FOR_COMMUNICATION,
    {
      OrganizationId: userData?.OrganizationId,
      ...payload,
    },
  );
  assertApiSuccess(response, "Could not load communication templates.");
  return getDataset(response);
}

export async function fetchCommunicationTemplates(userData) {
  const dataset = await templateForCommunication(userData, {
    ActionType: TEMPLATE_COMM_ACTIONS.LIST,
  });
  return getTableRows(dataset, ["SuccessStory"], 0);
}

export async function fetchCommunicationTemplateById(userData, templateId, templateType) {
  const dataset = await templateForCommunication(userData, {
    ActionType: TEMPLATE_COMM_ACTIONS.BY_ID,
    TemplateId: templateId,
    TemplateType: templateType,
  });
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? null;
}

export async function volunteerByParticipant(userData, payload = {}) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.VOLUNTEER_BY_PARTICIPANT, {
    ...baseContext(userData),
    ...payload,
  });
  assertApiSuccess(response, "Could not apply communication filter.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["SuccessStory"], 0);
}

export async function sendSmsEmailToVolunteers(userData, payload) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SEND_SMS_EMAIL_TO_VOLUNTEER, {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
    ...payload,
  });
  assertApiSuccess(response, "Could not send communication.");
  return getDataset(response);
}

export async function sendSmsEmailToTestUser(userData, payload) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SEND_SMS_EMAIL_TO_TEST_USER, {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
    ...payload,
  });
  assertApiSuccess(response, "Could not send test communication.");
  return getDataset(response);
}

export async function fetchEthnicityList(userData) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.ETHNICITY, {
    ...baseContext(userData),
    ActionType: "GetEthnicityList",
  });
  assertApiSuccess(response, "Could not load ethnicity list.");
  const dataset = getDataset(response);
  let ethnicities = getTableRows(
    dataset,
    ["EthniCityList", "EthnicityList", "aqEthnicity"],
    1,
  );
  // Named-table miss (Table/Table1 / casing): scan keys that look like ethnicity.
  if (!ethnicities.length && dataset && typeof dataset === "object") {
    for (const [key, value] of Object.entries(dataset)) {
      if (!/ethni/i.test(String(key))) continue;
      if (Array.isArray(value) && value.length) {
        ethnicities = value;
        break;
      }
      if (Array.isArray(value?.Rows) && value.Rows.length) {
        ethnicities = value.Rows;
        break;
      }
    }
  }
  return {
    volunteerNo: getTableRows(dataset, ["EmployeeNo", "SetUniqueCode"], 0)[0] ?? null,
    ethnicities,
  };
}

/**
 * Legacy ethnicity dropdown: optgroups by EthnicityName, option text = EthenicityReason,
 * plus trailing "Add New". Falls back to EthnicityName when a group has no reason rows.
 */
export function mapEthnicityOptions(ethnicities, { includeAddNew = true } = {}) {
  const byName = new Map();
  for (const row of ethnicities || []) {
    const rawId = String(row.EthnicityId ?? row.ethnicityId ?? "").trim();
    if (!rawId || rawId.toLowerCase() === "addnew") continue;
    const reason = String(
      row.EthenicityReason ||
        row.EthnicityReason ||
        row.ethenicityReason ||
        row.ethnicityReason ||
        "",
    ).trim();
    const name = String(
      row.EthnicityName || row.ethnicityName || "",
    ).trim() || "Other";
    if (!byName.has(name)) byName.set(name, []);
    byName.get(name).push({
      value: rawId.toLowerCase(),
      reason,
      name,
    });
  }

  const groups = [];
  const options = [];
  for (const [name, items] of byName) {
    const withReason = items.filter((item) => item.reason);
    const source = withReason.length ? withReason : items;
    const seen = new Set();
    const groupOptions = [];
    for (const item of source) {
      if (seen.has(item.value)) continue;
      seen.add(item.value);
      const label = item.reason || item.name;
      if (!label) continue;
      groupOptions.push({ value: item.value, label });
    }
    if (!groupOptions.length) continue;
    groups.push({ label: name, options: groupOptions });
    options.push({
      value: `__group__${name}`,
      label: name,
      disabled: true,
    });
    groupOptions.forEach((opt) => options.push(opt));
  }

  if (includeAddNew) {
    options.push({ value: "AddNew", label: "Add New" });
  }

  return { groups, options };
}

export async function fetchPlannerStudyList(userData) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.GET_STUDY_SCHEDULE_DATA, {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
    Action: "GetPlannerstudyListdata",
  });
  assertApiSuccess(response, "Could not load study list.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["StudyList"], 0);
}

/**
 * Groups + visits for a planner study schedule id.
 * Must use StudyScheduleId (not Study master id) — same as ePlanner MultiStudyVisitList.
 */
export async function fetchBulkCommGroupsAndVisits(userData, studyScheduleId) {
  const id = String(studyScheduleId || "").trim().toUpperCase();
  if (!id) return { groups: [], visits: [] };
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_CALENDAR_VIEW, {
    OrganizationId: String(userData?.OrganizationId || "").trim().toUpperCase(),
    CreatedBy: String(userData?.StaffId || "").trim().toUpperCase(),
    StaffIdRef: String(userData?.StaffId || "").trim().toUpperCase(),
    StudyIds: [id],
    ActionType: "MultiStudyVisitList",
    VisitId: null,
    GroupId: null,
    StartDate: null,
    EndDate: null,
  });
  assertApiSuccess(response, "Could not load study groups and visits.");
  const dataset = getDataset(response);
  return {
    groups: getTableRows(dataset, ["Groups", "groups"], 0),
    visits: getTableRows(dataset, ["Visits", "visits", "VisitList"], 1),
  };
}

/** Appointment-filtered volunteer list for bulk email/SMS. */
export async function fetchStudyWiseVolunteers(userData, filters = {}) {
  const nullIfBlank = (value) => {
    if (value == null) return null;
    const text = String(value).trim();
    return text ? text : null;
  };

  const visitIds = Array.isArray(filters.VisitIds)
    ? filters.VisitIds.map((id) => (id == null ? "" : String(id)))
    : [];

  const response = await api.post(API_ENDPOINTS.SETTINGS.STUDY_WISE_VOLUNTEERS, {
    ...baseContext(userData),
    StudyId: nullIfBlank(filters.StudyId),
    VisitIds: visitIds,
    AppointmentStatus: nullIfBlank(filters.AppointmentStatus),
    VolunteerStatus: nullIfBlank(filters.VolunteerStatus),
    AppointmentType: nullIfBlank(filters.AppointmentType),
    ActionType: nullIfBlank(filters.ActionType) ?? "",
    // SP treats only NULL as "no date filter"; '' maps to 1900-01-01 and returns no rows.
    StartPeriod: nullIfBlank(filters.StartPeriod),
    EndPeriod: nullIfBlank(filters.EndPeriod),
  });
  assertApiSuccess(response, "Could not load filtered participants.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["SuccessStory"], 0);
}

/**
 * Legacy Settings/SendEmailSMS — bulk send from appointment filter grid.
 * Payload: { FiltersList, IsEmail, IsSMS, EmailBody, SMSBody, EmailSubject, ... }
 */
export async function sendBulkEmailSms(userData, payload) {
  const response = await api.post(API_ENDPOINTS.SETTINGS.SEND_EMAIL_SMS, {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
    ...payload,
  });
  assertApiSuccess(response, "Could not send bulk communication.");
  return response;
}

export async function fetchClinicalDetails(userData, volunteerDemRecordId, actionType = "GetClinicalDetails") {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.CLINICAL_DETAILS_PER_VOL, {
    ...baseContext(userData),
    VolunteerDemRecordId: volunteerDemRecordId,
    ActionType: actionType,
  });
  assertApiSuccess(response, "Could not load clinical details.");
  return getDataset(response);
}

/** Full clinical info pack (includes Document / StudyListPervol tables). */
export async function fetchClinicalInfoPerVol(userData, volunteerDemRecordId) {
  return fetchClinicalDetails(userData, volunteerDemRecordId, "GetClinicalinfoPerVol");
}

export async function fetchVolunteerDocuments(userData, volunteerDemRecordId) {
  const dataset = await fetchClinicalInfoPerVol(userData, volunteerDemRecordId);
  return getTableRows(dataset, ["Document"], 14);
}

function studyInfoCount(row, ...keys) {
  if (!row) return null;
  for (const key of keys) {
    const raw = row[key];
    if (raw === undefined || raw === null || String(raw).trim() === "") continue;
    const num = Number(raw);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

export async function fetchVolunteerStudySummary(userData, volunteerDemRecordId) {
  const dataset = await fetchClinicalInfoPerVol(userData, volunteerDemRecordId);
  // StudyInfo is a single summary row (TotalStudy / TotalLive / TotalArchiveStudy),
  // not a list of live studies — same as legacy ViewVolunteer / PM_ViewParticipant.
  const studyInfo = getTableRows(dataset, ["StudyInfo"], 12)[0] ?? null;
  const studies = getTableRows(dataset, ["StudyListPervol"], 9);
  const archivedStudies = getTableRows(dataset, ["ArchiveStudyInfo"], 13);
  const totalFromInfo = studyInfoCount(studyInfo, "TotalStudy", "totalStudy");
  const liveFromInfo = studyInfoCount(studyInfo, "TotalLive", "totalLive");
  const archivedFromInfo = studyInfoCount(
    studyInfo,
    "TotalArchiveStudy",
    "totalArchiveStudy",
  );
  return {
    studies,
    archivedStudies,
    studyInfo,
    studyCounts: {
      total:
        totalFromInfo ??
        (Array.isArray(studies) ? studies.length : 0),
      live: liveFromInfo ?? 0,
      archived:
        archivedFromInfo ??
        (Array.isArray(archivedStudies) ? archivedStudies.length : 0),
    },
    documents: getTableRows(dataset, ["Document"], 14),
    appointmentInfo:
      getTableRows(dataset, ["AppointmentInfo"], 10)[0] ?? null,
    studyPaymentInfo: studyInfo,
    /** App list for volunteer notes (legacy AppList table). */
    appList: getTableRows(dataset, ["AppList"], 16),
    paymentEmailDetails:
      getTableRows(dataset, ["PaymentEmailDetails"], 17)[0] ?? null,
  };
}

/**
 * Legacy ChangeStatus rule (usp_ERec_VolunteerRegistration_NEWUI):
 * Inactive (1) / Removed (2) are blocked while the volunteer has Confirmed appointments.
 */
export function buildStatusChangeBlockedMessage(studyNames = []) {
  const unique = [
    ...new Set(
      (Array.isArray(studyNames) ? studyNames : [])
        .map((name) => String(name || "").trim())
        .filter(Boolean),
    ),
  ];
  if (unique.length === 0) {
    return "The participant has appointments booked. Please complete or cancel appointments assigned to this volunteer before changing the status to Inactive or Removed.";
  }
  const studyWord = unique.length > 1 ? "studies" : "study";
  return `The participant has appointments booked for ${studyWord} ${unique.join(", ")}. Please complete or cancel appointments assigned to this volunteer before changing the status to Inactive or Removed.`;
}

/** Sync check against a study-summary payload (AppointmentInfo.Confirmed). */
export function getStatusChangeValidationFromSummary(summary, nextStatus) {
  const statusCode = Number(nextStatus);
  if (statusCode !== 1 && statusCode !== 2) {
    return { allowed: true, message: "" };
  }
  const info = summary?.appointmentInfo || {};
  const confirmed = Number(info.Confirmed ?? info.confirmed ?? 0) || 0;
  if (confirmed <= 0) {
    return { allowed: true, message: "" };
  }
  const studyNames = (Array.isArray(summary?.studies) ? summary.studies : [])
    .map((row) => row?.StudyName ?? row?.studyName)
    .filter(Boolean);
  return {
    allowed: false,
    message: buildStatusChangeBlockedMessage(studyNames),
  };
}

/**
 * Pre-validate Inactive / Removed — same Confirmed-appointment gate as the SP.
 * Call before showing the confirm dialog so users see the block immediately.
 */
export async function validateVolunteerStatusChange(
  userData,
  volunteerDemRecordId,
  nextStatus,
) {
  const statusCode = Number(nextStatus);
  if (statusCode !== 1 && statusCode !== 2) {
    return { allowed: true, message: "" };
  }
  if (!volunteerDemRecordId) {
    return { allowed: false, message: "Participant not found." };
  }
  const summary = await fetchVolunteerStudySummary(
    userData,
    volunteerDemRecordId,
  );
  return getStatusChangeValidationFromSummary(summary, statusCode);
}

/** Appointments for Add note → Appointment notes dropdown. */
export async function fetchVolunteerAppList(userData, volunteerDemRecordId) {
  const dataset = await fetchClinicalInfoPerVol(userData, volunteerDemRecordId);
  return getTableRows(dataset, ["AppList"], 16);
}

/**
 * Legacy Study History: appointments + payments per study
 * (ActionType VisitWiseStudyInfo → GetCalendarViewAPI).
 */
export async function fetchVisitWiseStudyInfo(
  userData,
  volunteerId,
  studyIds = [],
) {
  const ids = (Array.isArray(studyIds) ? studyIds : [studyIds])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  if (!ids.length) {
    return { studies: [], appointments: [] };
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_CALENDAR_VIEW, {
    ...baseContext(userData),
    StudyIds: ids.map((id) => id.toUpperCase()),
    LocationIds: null,
    GroupId: null,
    VisitId: null,
    VolunteerId: String(volunteerId || "").toUpperCase(),
    StudyId: null,
    StartDate: null,
    EndDate: null,
    ActionType: "VisitWiseStudyInfo",
    IsListClicked: false,
  });
  assertApiSuccess(response, "Could not load study history.");
  const dataset = getDataset(response);
  return {
    studies: getTableRows(dataset, ["StudyList"], 0),
    appointments: getTableRows(dataset, ["AppointmentsData"], 1),
  };
}

export async function saveAppointmentPayment(userData, payload) {
  const response = await api.post(
    API_ENDPOINTS.ERECRUITMENT.SAVE_APPOINTMENT_PAYMENT,
    {
      ...payload,
      OrganizationId: userData?.OrganizationId,
      CreatedBy: userData?.StaffId,
      ActionType: payload?.ActionType || "Save",
    },
  );
  assertApiSuccess(response, "Could not save payment.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? dataset;
}

export async function sendPaymentEmail(userData, payload) {
  const response = await api.post(
    API_ENDPOINTS.ERECRUITMENT.SEND_PAYMENT_EMAIL,
    {
      ...payload,
      OrganizationId: userData?.OrganizationId,
      CreatedBy: userData?.StaffId,
      ActionType: payload?.ActionType || "SendPaymentEmail",
    },
  );
  assertApiSuccess(response, "Could not send payment email.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? dataset;
}

export async function saveVolunteerDocument(userData, {
  volunteerIdRef,
  documentName,
  documentUrl,
  extension,
}) {
  const createdBy =
    userData?.StaffId ||
    userData?.staffId ||
    userData?.UserId ||
    userData?.userId ||
    "";
  const ext = String(extension || "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SAVE_VOLUNTEER_DOCUMENT, {
    VolunteerIdRef: volunteerIdRef,
    DocumentName: documentName,
    DocumentUrl: documentUrl,
    OrganizationIdRef: userData?.OrganizationId,
    CreatedBy: createdBy,
    Extension: ext,
  });
  assertApiSuccess(response, "Could not save participant document.");
  return getDataset(response);
}

/** Fix legacy rows stored as blobHost + data:base64 so OnlyOffice can preview (same as QMS). */
export async function repairVolunteerDocuments(userData, volunteerIdRef) {
  const response = await api.post(
    API_ENDPOINTS.ERECRUITMENT.REPAIR_VOLUNTEER_DOCUMENTS,
    {
      VolunteerIdRef: volunteerIdRef,
      OrganizationIdRef: userData?.OrganizationId,
      CreatedBy: userData?.StaffId || userData?.staffId || "",
    },
  );
  assertApiSuccess(response, "Could not repair participant documents.");
  return getDataset(response);
}

export function isBrokenVolunteerDocumentUrl(url) {
  const text = String(url || "");
  return /data:/i.test(text);
}

/** Fields tracked for audit ChangedFormFields (legacy-compatible shape). */
const VOLUNTEER_AUDIT_FIELDS = [
  { id: "ForeName", keys: ["ForeName", "FirstName"], label: "First name" },
  { id: "SurName", keys: ["SurName", "LastName"], label: "Last name" },
  { id: "DOB", keys: ["DOB", "Dob"], label: "Date of birth" },
  { id: "Age", keys: ["Age"], label: "Age" },
  { id: "Gender", keys: ["Gender"], label: "Gender" },
  { id: "Ethnicity", keys: ["Ethnicity"], label: "Ethnicity" },
  {
    id: "EthnicityReason",
    keys: ["EthnicityReason"],
    label: "Other ethnicity",
  },
  { id: "ContactNumber", keys: ["ContactNumber"], label: "Mobile" },
  {
    id: "MobileCountryIso2",
    keys: ["MobileCountryIso2"],
    label: "Mobile country",
  },
  { id: "EmailId", keys: ["EmailId"], label: "Email" },
  {
    id: "WorkEmailId",
    keys: ["WorkEmailId", "PEmailId"],
    label: "Work email",
  },
  {
    id: "LandlineNumber",
    keys: ["LandlineNumber"],
    label: "Landline",
  },
  { id: "AddressLine1", keys: ["AddressLine1"], label: "Address line" },
  { id: "AddressLine2", keys: ["AddressLine2"], label: "Address line 2" },
  { id: "City", keys: ["City"], label: "City" },
  { id: "PostCode", keys: ["PostCode"], label: "Post code" },
  { id: "Country", keys: ["Country"], label: "Country" },
  { id: "NHSNumber", keys: ["NHSNumber"], label: "NHS number" },
  { id: "NINumber", keys: ["NINumber"], label: "NI number" },
  {
    id: "NextofKinName",
    keys: ["NextofKinName", "KinName"],
    label: "Next of kin",
  },
  {
    id: "NextofKinContact",
    keys: ["NextofKinContact", "KinContactNo"],
    label: "Kin contact",
  },
  {
    id: "IsVolunteerDatabase",
    keys: ["IsVolunteerDatabase", "IsUseVolunteerDB"],
    label: "Participant DB",
  },
  {
    id: "IsEmail",
    keys: ["IsEmail"],
    label: "Receive Email",
  },
  {
    id: "IsSMS",
    keys: ["IsSMS"],
    label: "Receive SMS",
  },
  {
    id: "IsPost",
    keys: ["IsPost"],
    label: "Receive Post",
  },
  {
    id: "ProfilePictureUrl",
    keys: ["ProfilePictureUrl", "ProfileImage", "profilePictureUrl"],
    label: "Profile picture",
  },
];

/** Coerce Yes/No / 1/0 / bool from VolunteerByVolIdinfo into a boolean. */
export function coerceVolunteerPrefFlag(value, defaultWhenMissing = false) {
  if (value === undefined || value === null || value === "") {
    return defaultWhenMissing;
  }
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const key = String(value).trim().toLowerCase();
  if (key === "yes" || key === "y" || key === "1" || key === "true") return true;
  if (key === "no" || key === "n" || key === "0" || key === "false") return false;
  return Boolean(value);
}

function readVolunteerField(row, keys) {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== "") return row[key];
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row || {}, key)) return row[key];
  }
  return "";
}

function normalizeAuditCompareValue(fieldId, value) {
  if (
    fieldId === "IsVolunteerDatabase" ||
    fieldId === "IsUseVolunteerDB" ||
    fieldId === "IsEmail" ||
    fieldId === "IsSMS" ||
    fieldId === "IsPost"
  ) {
    const key = String(value ?? "")
      .trim()
      .toLowerCase();
    if (
      key === "yes" ||
      key === "y" ||
      key === "1" ||
      key === "true" ||
      key === "on list" ||
      key === "in participant db"
    ) {
      return "1";
    }
    if (
      key === "no" ||
      key === "n" ||
      key === "0" ||
      key === "false" ||
      key === "not listed" ||
      key === "not in participant db"
    ) {
      return "0";
    }
    return String(Number(value) ? 1 : 0);
  }
  if (fieldId === "ProfilePictureUrl") {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    if (/^data:image\//i.test(raw) || raw.length > 200) return "__image__";
    return raw;
  }
  return String(value ?? "").trim();
}

function formatAuditDisplayValue(fieldId, value) {
  if (fieldId === "IsVolunteerDatabase" || fieldId === "IsUseVolunteerDB") {
    const normalized = normalizeAuditCompareValue(fieldId, value);
    return normalized === "1" ? "In Participant DB" : "Not in Participant DB";
  }
  if (fieldId === "IsEmail" || fieldId === "IsSMS" || fieldId === "IsPost") {
    return normalizeAuditCompareValue(fieldId, value) === "1" ? "Yes" : "No";
  }
  if (fieldId === "ProfilePictureUrl") {
    const raw = String(value ?? "").trim();
    if (!raw) return "No photo";
    if (/^data:image\//i.test(raw) || raw.length > 200) return "New photo";
    return "Existing photo";
  }
  const text = String(value ?? "").trim();
  return text || "—";
}

/**
 * Build legacy-compatible ChangedFormFields for individual profile edits.
 * Shape: [{ fieldType, fieldId, label, oldValue, newValue }]
 */
export function buildVolunteerChangedFormFields(volunteer, patch = {}) {
  const row = volunteer || {};
  const changes = [];

  for (const field of VOLUNTEER_AUDIT_FIELDS) {
    const patchKey = field.keys.find((key) =>
      Object.prototype.hasOwnProperty.call(patch || {}, key),
    );
    if (!patchKey) continue;

    const oldRaw = readVolunteerField(row, field.keys);
    const newRaw = patch[patchKey];
    const oldCompare = normalizeAuditCompareValue(field.id, oldRaw);
    const newCompare = normalizeAuditCompareValue(field.id, newRaw);
    if (oldCompare === newCompare) continue;

    const isCheckbox =
      field.id === "IsVolunteerDatabase" ||
      field.id === "IsEmail" ||
      field.id === "IsSMS" ||
      field.id === "IsPost";
    changes.push({
      fieldType: isCheckbox ? "checkbox" : "Text",
      fieldId: field.id,
      label: field.label,
      oldValue: isCheckbox
        ? oldCompare === "1"
        : formatAuditDisplayValue(field.id, oldRaw),
      newValue: isCheckbox
        ? newCompare === "1"
        : formatAuditDisplayValue(field.id, newRaw),
    });
  }

  return changes;
}

/** Build UpdateVolunteer payload from a profile row + field patch. */
export function buildVolunteerUpdatePayload(volunteer, patch = {}) {
  const row = volunteer || {};
  const next = { ...row, ...patch };
  const age =
    next.Age != null && next.Age !== ""
      ? Number(next.Age)
      : undefined;

  const changedFormFields = buildVolunteerChangedFormFields(row, patch);

  const payload = {
    VolunteerDemRecordId:
      pick(next, "VolunteerDemRecordId", "volunteerDemRecordId") || "",
    ForeName: pick(next, "ForeName", "FirstName", "foreName", "firstName") || "",
    SurName: pick(next, "SurName", "LastName", "surName", "lastName") || "",
    ContactNumber: pick(next, "ContactNumber", "contactNumber") || "",
    MobileCountryIso2: (
      pick(next, "MobileCountryIso2", "mobileCountryIso2") || "GB"
    )
      .toString()
      .trim()
      .toUpperCase() || "GB",
    EmailId: pick(next, "EmailId", "emailId") || "",
    WorkEmailId: pick(next, "WorkEmailId", "PEmailId", "workEmailId") || "",
    LandlineNumber: pick(next, "LandlineNumber", "landlineNumber") || "",
    DOB: pick(next, "DOB", "Dob", "dob") || "",
    Age: Number.isFinite(age) ? age : 0,
    Gender: pick(next, "Gender", "gender") || "",
    NHSNumber: pick(next, "NHSNumber", "nhsNumber") || "",
    NINumber: pick(next, "NINumber", "niNumber") || "",
    IsVerify: Number(next.IsVerify ?? 0) ? 1 : 0,
    IsVerifyNHS: Number(next.IsVerifyNHS ?? 0) ? 1 : 0,
    NextofKinName:
      pick(next, "NextofKinName", "KinName", "nextofKinName") || "",
    NextofKinContact:
      pick(next, "NextofKinContact", "KinContactNo", "nextofKinContact") || "",
    IsVolunteerDatabase: (() => {
      if (next.IsVolunteerDatabase != null && next.IsVolunteerDatabase !== "") {
        return Number(next.IsVolunteerDatabase) ? 1 : 0;
      }
      const db = String(next.IsUseVolunteerDB ?? "").trim().toLowerCase();
      return db === "yes" ||
        db === "1" ||
        db === "true" ||
        db === "on list" ||
        db === "in participant db"
        ? 1
        : 0;
    })(),
    Ethnicity: pick(next, "Ethnicity", "ethnicity") || "",
    EthnicityReason: pick(next, "EthnicityReason", "ethnicityReason") || "",
    AddressLine1: pick(next, "AddressLine1", "addressLine1") || "",
    AddressLine2: pick(next, "AddressLine2", "addressLine2") || "",
    City: pick(next, "City", "city") || "",
    PostCode: pick(next, "PostCode", "postCode") || "",
    Country: pick(next, "Country", "country") || "",
    // Profile API returns Yes/No strings — coerce so "No" is not treated as true.
    IsEmail: coerceVolunteerPrefFlag(next.IsEmail, false),
    IsSMS: coerceVolunteerPrefFlag(next.IsSMS, false),
    IsPost: coerceVolunteerPrefFlag(next.IsPost, false),
    // Legacy audit trail — which individual fields changed in this save.
    ChangedFormFields: JSON.stringify(changedFormFields),
    Reason: pick(patch, "Reason", "reason") || "",
  };

  // Match legacy Web SaveVolunteers: only send a new image payload when the caller
  // explicitly patched a data-URL / raw base64. Otherwise send "" so the SP keeps
  // the existing blob path. Re-sending the display URL (often a long absolute CDN
  // URL) overwrites/truncates the stored path and the photo disappears.
  const patchPicture = String(
    pick(
      patch,
      "ProfilePictureUrl",
      "profilePictureUrl",
      "ProfileImage",
      "profileImage",
    ) || "",
  ).trim();
  const isNewUpload =
    /^data:image\//i.test(patchPicture) ||
    (patchPicture.length > 200 &&
      !/^https?:\/\//i.test(patchPicture) &&
      !patchPicture.includes("/"));
  payload.ProfilePictureUrl = isNewUpload ? patchPicture : "";

  return payload;
}

export async function fetchMedicationHistory(
  userData,
  volunteerIdRef,
  medicationHistoryId = null,
) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.GET_MEDICATION_HISTORY, {
    OrganizationId: userData?.OrganizationId,
    CreatedBy: userData?.StaffId,
    VolunteerId: volunteerIdRef,
    MedicationHistoryId: medicationHistoryId || null,
    ActionType: "GetMedicationHistory",
  });
  assertApiSuccess(response, "Could not load medication history.");
  const dataset = getDataset(response);
  return getTableRows(dataset, ["MedicationHistory", "SuccessStory"], 0);
}

/** Full clinical pack from GetClinicalinfoPerVol (legacy profile bind). */
export async function loadParticipantClinicalPack(userData, volunteerDemRecordId) {
  const dataset = await fetchClinicalInfoPerVol(userData, volunteerDemRecordId);
  return {
    medical: getTableRows(dataset, ["MedicalHistory"], 2),
    surgical: getTableRows(dataset, ["SurgicalHistory"], 3),
    family: getTableRows(dataset, ["FamilyHistory"], 4),
    allergy: getTableRows(dataset, ["AllergyHistory"], 5),
    physical: getTableRows(dataset, ["PhysicalMeasurementHistory"], 6),
    medication: getTableRows(dataset, ["MedicationHistory"], 8),
    snomedTerms: getTableRows(dataset, ["SenomedTerm"], 1),
    medicationSnomedTerms: getTableRows(dataset, ["MedicationSnomedTerm"], 7),
  };
}

function snomedRowsFromResponse(response) {
  const dataset = getDataset(response);
  if (Array.isArray(dataset)) return dataset;
  // External SnomedFilter often returns { data: [ { ConceptId, Term } ] } already unwrapped by getDataset.
  if (dataset && typeof dataset === "object") {
    if (Array.isArray(dataset.data)) return dataset.data;
    if (Array.isArray(dataset.Data)) return dataset.Data;
  }
  const named = getTableRows(dataset, ["SenomedTerm", "SuccessStory", "Table"], 0);
  if (named.length) return named;
  if (dataset && typeof dataset === "object") {
    // DataTable JSON can be column→arrays; prefer row-shaped arrays.
    const values = Object.values(dataset);
    const firstArr = values.find(
      (v) =>
        Array.isArray(v) &&
        v.length &&
        typeof v[0] === "object" &&
        (v[0].Term != null ||
          v[0].term != null ||
          v[0].ConceptId != null ||
          v[0].SnomedCode != null),
    );
    if (firstArr) return firstArr;
  }
  return [];
}

/** Local lookup terms (Route / Unit / Frequency / Relationship) via DB SearchSnomed. */
const LOCAL_SNOMED_TYPES = new Set([
  "route",
  "unit",
  "frequency",
  "relationship",
  "adverse",
]);

/** External THS autocomplete subsets (legacy Web SnomedFilter). */
const EXTERNAL_SNOMED_TYPES = new Set([
  "all", // Feasibility medical include/exclude
  "medicalhistory",
  "surgicalhistory",
  "familyhistory",
  "allergy",
  "medication",
]);

function recentTermTypeFor(termType) {
  const key = String(termType || "").trim();
  const map = {
    MedicalHistory: "Medical",
    SurgicalHistory: "Surgical",
    FamilyHistory: "Family",
    Allergy: "Allergy",
    Medication: "Medication",
  };
  return map[key] || key;
}

/** DB SearchSnomed (Route, Unit, Frequency, …). */
export async function searchSnomedLocal(userData, { searchText = "", termType = "" } = {}) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SEARCH_SNOMED, {
    OrganizationId: userData?.OrganizationId,
    SearchText: String(searchText || ""),
    TermType: String(termType || ""),
  });
  return snomedRowsFromResponse(response);
}

/** External SNOMED filter (MedicalHistory / SurgicalHistory / FamilyHistory / Allergy / Medication). */
export async function searchSnomedFilter({
  searchText = "",
  termType = "",
  moreTerm = "",
} = {}) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.SNOMED_FILTER, {
    SearchText: String(searchText || ""),
    TermType: String(termType || ""),
    MoreTerm: String(moreTerm || ""),
  });
  return snomedRowsFromResponse(response);
}

/** Recent / org terms from ClinicalDetailsPerVol (empty-box legacy behaviour). */
export async function searchClinicalRecentTerms(userData, { searchText = "", termType = "" } = {}) {
  const type = String(termType || "").trim();
  const isMedication = type.toLowerCase() === "medication";
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.CLINICAL_DETAILS_PER_VOL, {
    ...baseContext(userData),
    ActionType: isMedication ? "MedicationNameSearch" : "SearchTerm",
    SearchText: String(searchText || ""),
    TermType: isMedication ? undefined : recentTermTypeFor(type),
  });
  return snomedRowsFromResponse(response);
}

/**
 * Unified clinical term search — mirrors legacy ClinicalDetail.js:
 * - Medical/Surgical/Family/Allergy/Medication (≥3 chars) → SnomedFilter
 * - empty query → recent local terms
 * - Route/Unit/Frequency → SearchSnomed DB
 */
export async function searchSnomed(userData, { searchText = "", termType = "" } = {}) {
  const type = String(termType || "").trim();
  const text = String(searchText || "").trim();
  const typeKey = type.toLowerCase();

  // Feasibility medical search uses TermType "All" (legacy SnomedFilter).
  if (typeKey === "all") {
    if (text.length < 3) return [];
    return searchSnomedFilter({ searchText: text, termType: "All" });
  }

  if (EXTERNAL_SNOMED_TYPES.has(typeKey)) {
    if (!text) {
      return searchClinicalRecentTerms(userData, { searchText: "", termType: type });
    }
    if (text.length < 3) return [];
    return searchSnomedFilter({ searchText: text, termType: type });
  }

  if (LOCAL_SNOMED_TYPES.has(typeKey) || type) {
    return searchSnomedLocal(userData, { searchText: text, termType: type });
  }

  return [];
}

/** @deprecated alias — use searchSnomed */
export async function searchSnomedDb(userData, opts) {
  return searchSnomedLocal(userData, opts);
}

export async function fetchFamilyRelations(userData) {
  const response = await api.post(API_ENDPOINTS.ERECRUITMENT.CLINICAL_DETAILS_PER_VOL, {
    ...baseContext(userData),
    ActionType: "GetFamilyRelation",
  });
  if (!utils.isApiSuccess(response)) return [];
  return getTableRows(getDataset(response), ["SenomedTerm"], 0);
}

async function postClinicalSave(endpoint, payload, fallbackMessage) {
  const response = await api.post(endpoint, payload);
  assertApiSuccess(response, fallbackMessage);
  return getSuccessStoryRow(getDataset(response)) || getDataset(response);
}

export async function saveMedicalHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.SAVE_MEDICAL_HISTORY,
    { ...baseContext(userData), ...payload },
    "Could not save medical history.",
  );
}

export async function saveSurgicalHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.SAVE_SURGICAL_HISTORY,
    { ...baseContext(userData), ...payload },
    "Could not save surgical history.",
  );
}

export async function saveFamilyHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.SAVE_FAMILY_HISTORY,
    { ...baseContext(userData), ...payload },
    "Could not save family history.",
  );
}

export async function saveAllergyHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.SAVE_ALLERGY_HISTORY,
    { ...baseContext(userData), ...payload },
    "Could not save allergy history.",
  );
}

export async function savePhysicalMeasurementHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.SAVE_PHYSICAL_MEASUREMENT,
    { ...baseContext(userData), ...payload },
    "Could not save physical measurement.",
  );
}

export async function saveMedicationHistory(userData, payload) {
  return postClinicalSave(
    API_ENDPOINTS.ERECRUITMENT.MEDICATION_HISTORY,
    { ...baseContext(userData), ...payload },
    "Could not save medication.",
  );
}

export async function archiveMedicationHistory(userData, { medicationHistoryId, volunteerId }) {
  return saveMedicationHistory(userData, {
    MedicationHistoryId: medicationHistoryId,
    VolunteerId: volunteerId,
    ActionType: "Archive",
    IsArchive: true,
  });
}

export function getSuccessStoryRow(dataset) {
  return getTableRows(dataset, ["SuccessStory"], 0)[0] ?? null;
}

function parseAllowEmailFlag(value) {
  if (value === true || value === 1) return true;
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

/** Org payment notification email template (Settings/PaymentTemplateActivity). */
export async function fetchPaymentEmailTemplate(userData) {
  const response = await api.post(API_ENDPOINTS.SETTINGS.PAYMENT_TEMPLATE_ACTIVITY, {
    ...baseContext(userData),
    TemplateId: null,
    EmailText: null,
    EmailSubject: null,
    IsAllowEmail: false,
    ActionType: "Get",
  });
  assertApiSuccess(response, "Unable to load payment email template.");
  const row = getSuccessStoryRow(getDataset(response));
  if (!row) {
    return {
      EmailSubject: "",
      EmailText: "",
      IsAllowEmail: false,
    };
  }
  return {
    EmailSubject: String(
      pick(row, "TemplateSubject", "EmailSubject", "templateSubject", "emailSubject") ||
        "",
    ),
    EmailText: String(
      pick(row, "TemplateText", "EmailText", "templateText", "emailText") || "",
    ),
    IsAllowEmail: parseAllowEmailFlag(
      pick(row, "IsAllowEmail", "isAllowEmail"),
    ),
  };
}

export async function savePaymentEmailTemplate(userData, template) {
  const response = await api.post(API_ENDPOINTS.SETTINGS.PAYMENT_TEMPLATE_ACTIVITY, {
    ...baseContext(userData),
    TemplateId: null,
    EmailSubject: String(template?.EmailSubject ?? "").trim(),
    EmailText: String(template?.EmailText ?? ""),
    IsAllowEmail: !!template?.IsAllowEmail,
    ActionType: "Insert",
  });
  assertApiSuccess(response, "Unable to save payment email template.");
  const story = getSuccessStoryRow(getDataset(response));
  return {
    message:
      pick(story, "MSG", "Msg", "msg", "Message", "message") ||
      utils.getApiResponseMessage(response, "Template has been saved successfully."),
  };
}

export function getVolunteerDisplayName(row) {
  const first = pick(row, "FirstName", "ForeName", "firstName", "foreName") || "";
  const last = pick(row, "LastName", "SurName", "lastName", "surName") || "";
  const combined = `${first} ${last}`.trim();
  return combined || pick(row, "VolunteerName", "volunteerName") || "—";
}

export function getVolunteerRowId(row) {
  return String(
    pick(row, "VolunteerDemRecordId", "volunteerDemRecordId", "VolunteerId", "volunteerId") ||
      "",
  ).trim();
}
